import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { operatorOptions } from "@features/creative-analytics/components/ca-creative-type-bar";
import { type MetricFilterOperator } from "@features/landing-page-analytics/types";
import {
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { SecondaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";

interface Props {
  onReset(): void;
  onApply(value: number, operator: MetricFilterOperator): void;
  onOpen(): void;
  onClose(): void;
  value?: number;
  operator?: MetricFilterOperator;
  isOpen: boolean;
}

export const NumericFilterPopover = ({
  onReset,
  onApply,
  onOpen,
  onClose,
  value,
  operator,
  isOpen,
}: Props) => {
  const [propsedValue, setProposedValue] = useState(String(value ?? ""));
  const [propsedOperator, setProposedOperator] = useState(
    operator ?? "greaterThan",
  );

  useEffect(() => {
    if (isOpen) {
      setProposedValue(String(value ?? ""));
    }
  }, [isOpen, value, setProposedValue]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (value) => (value ? onOpen() : onClose()),
    transform: false,
    placement: "bottom",
    middleware: [shift()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div>
      <button
        className={clsx(
          "flex justify-center items-center cursor-pointer",
          "rounded w-6 h-6 text-[0.65rem] ml-0.5",
          {
            "text-cosmic bg-cosmic-3": !isOpen,
            "text-white bg-primary": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <i className={clsx("fa-regular fa-sharp fa-filter")}></i>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            className={clsx(
              "absolute right-0 mt-2 px-4 pb-4 pt-3 w-52 z-[1000]",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <div className="mb-4">
              <label className="text-xs font-normal mb-1">LOGIC</label>
              <div>
                <Select
                  onChange={(value) => setProposedOperator(value)}
                  placeholder="Operator..."
                  options={operatorOptions}
                  value={propsedOperator}
                  menuClassName="w-[calc(var(--button-width)+10px)]"
                />
              </div>
            </div>
            <div className="nb-input-container">
              <label className="text-xs font-normal mb-1">VALUE</label>
              <div>
                <TextInput
                  className="w-full mt-2"
                  inputClassName="font-light"
                  placeholder="Filter value"
                  type="number"
                  value={propsedValue}
                  onChange={(e) => setProposedValue(e.target.value)}
                />
                <div className="text-xxs text-subtitle font-light mt-1">
                  For percentages, 1 is 100%
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <SecondaryButton
                className="flex-1 font-semibold"
                onClick={() => {
                  onReset();
                  onClose();
                }}
              >
                {value !== undefined ? "RESET" : "CANCEL"}
              </SecondaryButton>
              <SecondaryButton
                className="flex-1 font-semibold"
                isDisabled={!propsedValue}
                onClick={() => {
                  if (propsedValue && propsedOperator) {
                    onApply(Number(propsedValue), propsedOperator);
                  }
                  onClose();
                }}
              >
                APPLY
              </SecondaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
